import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { 
  Search, 
  X,
  Sparkles,
  Filter,
  ArrowLeft
} from 'lucide-react';
import { MultiInterviewSearchBar } from './MultiInterviewSearchBar';
import { SearchResultsList } from './SearchResultsList';
import { useMultiInterviewSearch, SearchResult } from '@/hooks/useMultiInterviewSearch';
import { useNavigate } from 'react-router-dom';

interface MultiInterviewSearchPanelProps {
  trigger?: React.ReactNode;
}

export const MultiInterviewSearchPanel = ({ trigger }: MultiInterviewSearchPanelProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  
  const {
    searchQuery,
    setSearchQuery,
    searchResults,
    isSearching,
    clearSearch,
    hasResults
  } = useMultiInterviewSearch();

  const handleResultClick = (result: SearchResult) => {
    // Navigate to the specific interview and highlight the note
    navigate(`/notespace/${result.noteSpaceId}?highlight=${result.id}`);
    setIsOpen(false);
  };

  const handleNavigateToInterview = (noteSpaceId: string, position: { x: number; y: number }) => {
    // Navigate to interview with specific position focus
    navigate(`/notespace/${noteSpaceId}?focus=${position.x},${position.y}`);
    setIsOpen(false);
  };

  const defaultTrigger = (
    <Button 
      variant="outline" 
      className="flex items-center space-x-2 hover:bg-blue-50 hover:border-blue-300"
    >
      <Search className="w-4 h-4" />
      <span>Search All Interviews</span>
      {hasResults && (
        <Badge variant="secondary" className="ml-1">
          {searchResults.length}
        </Badge>
      )}
    </Button>
  );

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        {trigger || defaultTrigger}
      </SheetTrigger>
      
      <SheetContent side="right" className="w-full sm:w-[600px] sm:max-w-[600px] flex flex-col p-0">
        <SheetHeader className="p-6 pb-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full">
                <Search className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <SheetTitle className="text-lg font-semibold text-gray-900">
                  Multi-Interview Search
                </SheetTitle>
                <p className="text-sm text-gray-600">
                  Find insights across all your interviews
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsOpen(false)}
              className="h-8 w-8 p-0"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </SheetHeader>

        <div className="p-6 border-b border-gray-200">
          <MultiInterviewSearchBar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onClear={clearSearch}
            isSearching={isSearching}
            resultsCount={searchResults.length}
          />
        </div>

        <div className="flex-1 overflow-hidden">
          {searchQuery ? (
            <SearchResultsList
              results={searchResults}
              searchQuery={searchQuery}
              onResultClick={handleResultClick}
              onNavigateToInterview={handleNavigateToInterview}
            />
          ) : (
            <div className="p-6 text-center">
              <div className="space-y-4">
                <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto">
                  <Sparkles className="w-8 h-8 text-blue-500" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Discover Insights Across Interviews
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Search through all your interviews to find patterns, quotes, and insights.
                  </p>
                </div>
                
                <div className="grid grid-cols-1 gap-3 max-w-sm mx-auto">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSearchQuery('type:pain')}
                    className="justify-start"
                  >
                    <Filter className="w-3 h-3 mr-2" />
                    Find all pain points
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSearchQuery('pricing')}
                    className="justify-start"
                  >
                    <Search className="w-3 h-3 mr-2" />
                    Search for "pricing"
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSearchQuery('type:quote')}
                    className="justify-start"
                  >
                    <Filter className="w-3 h-3 mr-2" />
                    Browse all quotes
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {searchQuery && (
          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between text-xs text-gray-600">
              <span>
                {isSearching ? 'Searching...' : `${searchResults.length} results found`}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearSearch}
                className="h-6 text-xs"
              >
                Clear search
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};